import { css } from "@emotion/react";

import { TooltipV3 } from "src/components/TooltipV3/TooltipV3.tsx";
import { type HTMLAttributes, useEffect, useRef } from "react";
import { type ExactTheme } from "src/lib/emotion/theme.ts";
import { LoadingIndicator } from "src/components/DataGrid/product/LoadingIndicator.tsx";
import { ColumnGroupingCell } from "src/components/DataGrid/product/ColumnGroupingCell.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useNonConflictingTheme } from "src/lib/emotion/use-non-conflicting-theme.ts";
import { getResourceFieldTranslation } from "src/system/getResourceFieldOptions.ts";
import { ResourceEnum } from "src/constants/constants.ts";
import { ProductType } from "@exonn/js-sdk";

export function ComplexProductSetGroupingCell(
  props: HTMLAttributes<HTMLButtonElement> & {
    isExpanded: boolean;
    isLastChild: boolean;
    hasChildren: boolean;
    isLoading?: boolean;
    error?: any;
  },
) {
  const {
    isExpanded,
    isLastChild,
    hasChildren,
    isLoading = false,
    error,
    ...rest
  } = props;
  const hasError = !!error;
  const { t } = useTranslation(TranslationNamespaceEnum.Common);
  return (
    <TooltipV3
      delayDuration={500}
      content={getResourceFieldTranslation({
        t,
        resource: ResourceEnum.Product,
        type: "option_label",
        field: "type",
        value: ProductType.SET,
      })}
      contentProps={{ align: "start" }}
    >
      <ColumnGroupingCell hasError={hasError} {...rest}>
        {isLoading && <LoadingIndicator />}
        {hasChildren && <div css={createTriangleIcon(isExpanded)} />}

        <div css={{ transform: "translateX(16px)", fontSize: 15 }}>
          <i className="icon-20-set" />
        </div>

        <TreePathCanvas isLastChild={isLastChild} isExpanded={isExpanded} />
      </ColumnGroupingCell>
    </TooltipV3>
  );
}

const createTriangleIcon =
  (isExpanded: boolean) =>
  ({ colorsV2 }: ExactTheme) =>
    css({
      position: "absolute",
      width: 10,
      height: 10,
      right: 0,
      bottom: isExpanded ? "unset" : 0,
      clipPath: `polygon(100% 100%, 100% 0, 0 ${isExpanded ? 0 : 100}%)`,
      backgroundColor: isExpanded ? colorsV2.blue[500] : colorsV2.grey[200],
      transition: "background-color 0.3s",
      zIndex: 1,
    });

export type DrawLeafPathProps = {
  isLastChild: boolean;
  isExpanded: boolean;
};

export const TreePathCanvas = (props: DrawLeafPathProps) => {
  const { isLastChild, isExpanded } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useNonConflictingTheme();

  useEffect(() => {
    if (!canvasRef.current) return;

    const { width, height } = canvasRef.current;
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);
    ctx.strokeStyle = theme.colorsV2.grey[200];
    ctx.lineWidth = 2;

    ctx.beginPath();

    ctx.moveTo(width / 2, 0);
    ctx.lineTo(width / 2, isLastChild ? height / 2 : height);

    ctx.moveTo(width / 2, height / 2);
    ctx.lineTo(width * 0.6, height / 2);

    if (isExpanded) {
      ctx.moveTo(width * 0.75, height * 0.65);
      ctx.lineTo(width * 0.75, height);
    }

    ctx.moveTo(width * 0.9, height / 2);
    ctx.lineTo(width, height / 2);

    ctx.stroke();
  }, [isLastChild, isExpanded, theme]);

  return (
    <canvas
      ref={canvasRef}
      width={60}
      height={theme.productDataGrid.rowHeight}
      css={{ position: "absolute", zIndex: 1 }}
    />
  );
};
